import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { toast } from '@/lib/v2/components';

import {
  atomActionsList,
  atomCurrentCampaignID,
  atomDetailCampaign,
  atomLoadingProcess,
  atomMoveCampaignPayload,
  atomMoveSuccess,
  atomRefetchAllData,
  atomRefetchDetailData,
  atomTotalsProcess,
} from '@/src/modules/CampaignsModule/atoms/campaignAtom';
import { useCampaignActionsStateContext } from '@/src/modules/CampaignsModule/contexts/CampaignActionsContext';

import { useCampaignActions } from './useCampaignActions';
import { useCampaign } from './useCampaigns';
import { useStrategies } from './useStrategies';

const TRANSLATE_MODAL_MOVE = 'CAMPAIGN_ACTIONS_MAIN.MOVE_ACTIONS.TOAST_MESSAGES';

export const useActionsData = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const { filterBy, orderBy, pageIndex, pageSize } = useCampaignActionsStateContext();
  const { getList } = useCampaignActions({
    campaignID: Number(id),
    campaignActionsStateContext: { filterBy, orderBy, pageIndex, pageSize },
  });
  const { getDetailCampaign } = useCampaign();
  const { getStrategies } = useStrategies();
  const [refetchAllData, setRefetchAllData] = useAtom(atomRefetchAllData);
  const setCurrentCampaignID = useSetAtom(atomCurrentCampaignID);

  const [actionsList, setActionsList] = useAtom(atomActionsList);
  const [refetchDetailData, setRefetchDetailData] = useAtom(atomRefetchDetailData);
  const detailCampaign = useAtomValue(atomDetailCampaign);

  const loadingProcess = useAtomValue(atomLoadingProcess);
  const totalsActionsList = useAtomValue(atomTotalsProcess);

  const [moveSuccess, setMoveSuccess] = useAtom(atomMoveSuccess);
  const [moveCampaignPayload, setMoveCampaignPayload] = useAtom(atomMoveCampaignPayload);

  const filtersCount = useMemo(() => Object.keys(filterBy).length, [filterBy]);

  useEffect(() => {
    setCurrentCampaignID(Number(id));
  }, [id, setCurrentCampaignID]);

  useEffect(() => {
    if (moveSuccess && moveCampaignPayload) {
      /** cspell:disable */
      const isSingle = moveCampaignPayload?.elementosId?.length === 1;
      /** cspell:enable */

      toast({
        title: isSingle
          ? t(`${TRANSLATE_MODAL_MOVE}.SUCCESS.SINGLE.title`)
          : t(`${TRANSLATE_MODAL_MOVE}.SUCCESS.MULTIPLE.title`),
        body: isSingle
          ? t(`${TRANSLATE_MODAL_MOVE}.SUCCESS.SINGLE.message`)
          : t(`${TRANSLATE_MODAL_MOVE}.SUCCESS.MULTIPLE.message`),
        variant: 'success',
      });

      setMoveSuccess(RESET);
      setMoveCampaignPayload(RESET);
    }
  }, [moveCampaignPayload, moveSuccess, setMoveCampaignPayload, setMoveSuccess, t]);

  useEffect(() => {
    if (refetchDetailData) {
      void getDetailCampaign({ campaignID: Number(id) });
      void getStrategies();
      setRefetchDetailData(RESET);
    }
  }, [getDetailCampaign, id, refetchDetailData, setRefetchDetailData, getStrategies]);

  useEffect(() => {
    if (!detailCampaign?.campaignTypeId) return;

    void getList();
  }, [detailCampaign?.campaignTypeId, getList, setActionsList]);

  useEffect(() => {
    if (!refetchAllData) return;
    void getList();
    setRefetchAllData(RESET);
  }, [refetchAllData, getList, setRefetchAllData]);

  return {
    actionsList,
    loadingProcess,
    totalsActionsList,
    filtersCount,
  };
};
